import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useRealtimeSync } from "@/hooks/use-realtime";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

import NotFound from "@/pages/not-found";
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Registrar from "@/pages/Registrar";
import Dashboard from "@/pages/Dashboard";
import Empresas from "@/pages/Empresas";
import Usuarios from "@/pages/Usuarios";
import Laudos from "@/pages/Laudos";
import RevisaoTecnica from "@/pages/RevisaoTecnica";
import LaudoDetalhes from "@/pages/LaudoDetalhes";
import Convites from "@/pages/Convites";
import Perfil from "@/pages/Perfil";
import Veiculos from "@/pages/Veiculos";
import RegistrarUsuario from "@/pages/RegistrarUsuario";
import { Auditoria } from "@/pages/Auditoria";
import Catalogo from "@/pages/Catalogo";
import Apks from "@/pages/Apks";
import Dossies from "@/pages/Dossies";
import DossieDetalhes from "@/pages/DossieDetalhes";
import DossieNovo from "@/pages/DossieNovo";
import Servicos from "@/pages/Servicos";
import DossiePreAnalise from "@/pages/DossiePreAnalise";
import OrdemServico from "@/pages/OrdemServico";
import DocumentosExternos from "@/pages/DocumentosExternos";
import VerificarAutenticidade from "@/pages/VerificarAutenticidade";

function ProtectedRoute({ component: Component, ...rest }: any) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      setLocation("/");
    }
  }, [loading, user, setLocation]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return <Component {...rest} />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={LandingPage} />
      <Route path="/login" component={Login} />
      <Route path="/verificar/:token" component={VerificarAutenticidade} />
      <Route path="/registrar" component={Registrar} />
      <Route path="/entrar" component={RegistrarUsuario} />
      <Route path="/dashboard">
        {() => <ProtectedRoute component={Dashboard} />}
      </Route>
      <Route path="/empresas">
        {() => <ProtectedRoute component={Empresas} />}
      </Route>
      <Route path="/usuarios">
        {() => <ProtectedRoute component={Usuarios} />}
      </Route>
      <Route path="/laudos">
        {() => <ProtectedRoute component={Laudos} />}
      </Route>
      <Route path="/dossies">
        {() => <ProtectedRoute component={Dossies} />}
      </Route>
      <Route path="/dossies/novo">
        {() => <ProtectedRoute component={DossieNovo} />}
      </Route>
      <Route path="/dossies/:id">
        {(params) => <ProtectedRoute component={DossieDetalhes} {...params} />}
      </Route>
      <Route path="/dossies/:id/pre-analise">
        {(params) => <ProtectedRoute component={DossiePreAnalise} {...params} />}
      </Route>
      <Route path="/servicos">
        {() => <ProtectedRoute component={Servicos} />}
      </Route>
      <Route path="/servicos/:tipo">
        {(params) => <ProtectedRoute component={Servicos} {...params} />}
      </Route>
      <Route path="/ordens-servico/:id">
        {(params) => <ProtectedRoute component={OrdemServico} {...params} />}
      </Route>
      <Route path="/laudos/revisao">
        {() => <ProtectedRoute component={RevisaoTecnica} />}
      </Route>
      <Route path="/laudos/:id">
        {(params) => <ProtectedRoute component={LaudoDetalhes} {...params} />}
      </Route>
      <Route path="/convites">
        {() => <ProtectedRoute component={Convites} />}
      </Route>
      <Route path="/perfil">
        {() => <ProtectedRoute component={Perfil} />}
      </Route>
      <Route path="/veiculos">
        {() => <ProtectedRoute component={Veiculos} />}
      </Route>
      <Route path="/catalogo">
        {() => <ProtectedRoute component={Catalogo} />}
      </Route>
      <Route path="/auditoria">
        {() => <ProtectedRoute component={Auditoria} />}
      </Route>
      <Route path="/owner/apks">
        {() => <ProtectedRoute component={Apks} />}
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function RealtimeSync() {
  useRealtimeSync();
  return null;
}

function InternalApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RealtimeSync />
        <TooltipProvider>
          <Router />
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function App() {
  return <Switch>
    <Route path="/documentos" component={DocumentosExternos} />
    <Route component={InternalApp} />
  </Switch>;
}

export default App;
